import { IconFile as FileIcon, IconPaperclip as Paperclip, IconX as X } from "@tabler/icons-react";
import { useRef, type ChangeEvent } from "react";
import {
  attachmentStatusLabel,
  formatFileSize,
  MAX_ATTACHMENTS_PER_PROMPT,
  type AttachmentChip,
} from "./attachments";

interface AttachmentPickerProps {
  disabled: boolean;
  count: number;
  onFilesSelected: (files: File[]) => void;
}

export function AttachmentPicker({ disabled, count, onFilesSelected }: AttachmentPickerProps) {
  const input = useRef<HTMLInputElement>(null);
  const atLimit = count >= MAX_ATTACHMENTS_PER_PROMPT;
  const selectFiles = (event: ChangeEvent<HTMLInputElement>) => {
    onFilesSelected(Array.from(event.currentTarget.files ?? []));
    // 允许老板移除失败项后再次手动选择同一个文件；这里不会触发自动重试。
    event.currentTarget.value = "";
  };
  return (
    <>
      <input
        ref={input}
        className="attachment-input"
        type="file"
        multiple
        tabIndex={-1}
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,text/markdown,text/csv,application/json,application/xml,text/xml,application/yaml,text/yaml,.md,.markdown,.yaml,.yml"
        aria-hidden="true"
        onChange={selectFiles}
      />
      <button
        type="button"
        className="attachment-picker"
        aria-label={atLimit ? "附件已达到 5 个上限" : "添加附件"}
        title={atLimit ? "每条消息最多 5 个附件" : "添加图片、PDF 或普通文件"}
        disabled={disabled || atLimit}
        onClick={() => input.current?.click()}
      >
        <Paperclip size={17} />
        <span>附件{count ? ` ${count}/${MAX_ATTACHMENTS_PER_PROMPT}` : ""}</span>
      </button>
    </>
  );
}

interface AttachmentChipsProps {
  attachments: AttachmentChip[];
  disabled: boolean;
  onRemove: (attachment: AttachmentChip) => void;
}

export function AttachmentChips({ attachments, disabled, onRemove }: AttachmentChipsProps) {
  if (!attachments.length) return null;
  return (
    <div className="attachment-chips" aria-label="待发送附件" aria-live="polite">
      {attachments.map((attachment) => (
        <div
          className={`attachment-chip attachment-chip--${attachment.status}`}
          key={attachment.clientId}
        >
          <FileIcon size={16} aria-hidden="true" />
          <span className="attachment-chip-copy">
            <strong title={attachment.filename}>{attachment.filename}</strong>
            <small>
              {formatFileSize(attachment.sizeBytes)} · {attachmentStatusLabel(attachment)}
            </small>
          </span>
          <button
            type="button"
            aria-label={`移除附件：${attachment.filename}`}
            title="移除附件"
            disabled={
              disabled || attachment.status === "uploading" || attachment.status === "removing"
            }
            onClick={() => onRemove(attachment)}
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
